import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { WalletAddressCreationType } from "@lib/types/Billing";
import { Form, Modal, Space } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import useIntl from "react-intl/lib/src/components/useIntl";
import { useParams } from "react-router-dom";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { FormattedMessage } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  WalletAddressCreationTypeInput,
  WalletAddressCustomIdInput,
  WalletAddressInput,
  WalletAddressNetworkInput,
  WalletAddressProtocolInput,
  WalletAddressStatusInput,
  WalletCustomerIdInput,
  WalletIdInput,
} from "../WalletAddressFormInputs";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import {
  AIWallet,
  AIWalletAddress,
  AIWalletAddressStatus,
} from "@lib/types/Wallet";
import {
  apiAddAIWalletAddress,
  apiAddMultipleAIWalletAddress,
  apiUpdateAIWalletAddress,
} from "@lib/api/walletAI";

const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 17 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  walletsQuery?: UseQueryProps<PaginationType<AIWallet>>;
  walletAddress?: Partial<AIWalletAddress>;
  customerId?: string;
};

export default function WalletAddressModal(props: Props) {
  const {
    openModal,
    toggleModal,
    walletsQuery,
    walletAddress,
    customerId: customerIdProp,
  } = props;

  const { messages } = useIntl();
  const [form] = Form.useForm();
  const formCustomerIdValue = Form.useWatch("customerId", form);
  const address = Form.useWatch("address", form);

  const initialValues = useMemo<Record<string, any>>(
    () => ({
      walletId: "",
      status: AIWalletAddressStatus.Available,
      creationType: WalletAddressCreationType.SINGLE,
      network: null,
      protocol: null,
      customId: null,
      address: "",
      customerId: customerIdProp ?? undefined,
    }),
    [customerIdProp],
  );

  const { walletId } = useParams();
  const [creationType, setCreationType] = useState<WalletAddressCreationType>(
    WalletAddressCreationType.SINGLE,
  );

  useEffect(() => {
    if (!openModal || !form) return;

    let obj: Record<string, any> = { ...initialValues };

    if (walletAddress) {
      obj = {
        ...walletAddress,
        walletId: walletAddress.walletId,
      };
    } else {
      obj.walletId = walletId ?? form.getFieldValue("walletId") ?? "";
    }

    form.setFieldsValue(obj);
  }, [openModal, walletAddress, walletId, initialValues, form]);

  const handleResetForm = useCallback(() => {
    form?.setFieldsValue(initialValues);
    setCreationType(WalletAddressCreationType.SINGLE);
  }, [form, initialValues]);

  const handleClose = useCallback(() => {
    toggleModal(false);
    handleResetForm();
  }, [toggleModal]);

  const queryClient = useQueryClient();
  const addMultipleQuery = useMutation({
    mutationFn: apiAddMultipleAIWalletAddress,
    mutationKey: ["ai-wallet-address"],
    onSuccess() {
      handleResetForm();
      toggleModal(false);
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.walletAddresses"] as string }}
        />,
      );
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("wallet");
        },
      });
      // ai-wallet-address-dropdown
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("ai-wallet-address-dropdown");
        },
      });
    },
  });
  const addQuery = useMutation({
    mutationFn: apiAddAIWalletAddress,
    mutationKey: ["ai-wallet-address"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.walletAddress"] as string }}
        />,
      );
      form.setFieldValue("address", "");
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("wallet");
        },
      });
      // ai-wallet-address-dropdown
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("ai-wallet-address-dropdown");
        },
      });
      handleClose();
    },
  });
  const updateQuery = useMutation({
    mutationFn: apiUpdateAIWalletAddress,
    mutationKey: ["ai-wallet-address"],
    onSuccess() {
      handleResetForm();
      toggleModal(false);
      toast.success(
        <FormattedMessage
          id="common.updatedMessage"
          values={{ name: messages["common.walletAddress"] as string }}
        />,
      );
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("wallet");
        },
      });
      // ai-wallet-address-dropdown
      queryClient.resetQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return JSON.stringify(key).includes("ai-wallet-address-dropdown");
        },
      });
    },
  });

  const mutationQuery = walletAddress ? updateQuery : addQuery;

  return (
    <Modal
      title={
        <FormattedMessage
          id={walletAddress ? "common.editTitle" : "common.createTitle"}
          values={{ title: messages["common.walletAddress"] as string }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "60%",
        xl: "50%",
        xxl: "40%",
      }}
    >
      <Form<AIWalletAddress>
        {...layout}
        form={form}
        name="add-ai-wallet-address"
        autoComplete="off"
        style={{ maxWidth: 800, marginTop: 20 }}
        initialValues={initialValues}
        onFinish={(values) => {
          if (
            !walletAddress &&
            creationType === WalletAddressCreationType.MULTIPLE
          ) {
            let customId = Number(values.customId);
            const addresses = address
              .split("\n")
              .filter((x) => !!x)
              .map((address) => ({
                customId: customId++,
                customerId: formCustomerIdValue,
                ...values,
                address,
              }));
            addMultipleQuery.mutate(addresses);
          } else {
            mutationQuery.mutate({
              customerId: formCustomerIdValue,
              ...values,
              id: walletAddress?.id,
            });
          }
        }}
      >
        <WalletIdInput
          walletsQuery={walletsQuery}
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
        />

        <WalletCustomerIdInput
          walletsQuery={walletsQuery}
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletAddress={walletAddress}
        />

        <WalletAddressStatusInput
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={walletAddress?.id}
        />

        <WalletAddressNetworkInput
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={walletAddress?.id}
        />

        <WalletAddressProtocolInput
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={walletAddress?.id}
        />

        <WalletAddressCreationTypeInput
          creationType={creationType}
          setCreationType={setCreationType as any}
          show={!walletAddress}
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
        />

        <WalletAddressCustomIdInput
          creationType={creationType}
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletsQuery={walletsQuery}
        />

        <WalletAddressInput
          creationType={creationType}
          form={form}
          loading={walletsQuery?.isLoading || mutationQuery.isPending}
          walletAddressId={walletAddress?.id}
        />

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            {!walletAddress && (
              <ActionButton
                type="reset"
                onClick={handleResetForm}
                loading={walletsQuery?.isLoading || mutationQuery.isPending}
              />
            )}
            <ActionButton
              type={!!walletAddress ? "save" : "add"}
              loading={walletsQuery?.isLoading || mutationQuery.isPending}
            />
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
